// src/modules/ambassadors/components/AmbassadorDetailModal.tsx - Детальная информация об амбассадоре

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Tabs,
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Statistic,
  Row,
  Col,
  Card,
  message,
  InputNumber,
  Input,
  Empty,
} from 'antd';
import {
  CopyOutlined,
  DollarOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { AmbassadorDetail, AmbassadorTransaction, ClientAmbassadorLink } from '@/types/models/ambassador';
import {
  formatBonusBalance,
  formatPhoneDisplay,
  formatActivationDate,
  getTransactionTypeText,
  getTransactionTypeColor,
  formatTransactionAmount,
  copyPromoCodeToClipboard,
  getReferralLink,
  getQualificationLevelText,
  getQualificationLevelColor,
  formatCommissionRate,
} from '../utils/ambassadorUtils';
import { ambassadorsApi } from '@/services/api/ambassadors';

const { TabPane } = Tabs;

interface AmbassadorDetailModalProps {
  visible: boolean;
  onClose: () => void;
  ambassador: AmbassadorDetail | null;
  onBalanceAdjust?: (ambassadorId: number, amount: number, description: string) => Promise<any>;
}

export const AmbassadorDetailModal: React.FC<AmbassadorDetailModalProps> = ({
  visible,
  onClose,
  ambassador,
  onBalanceAdjust,
}) => {
  const [activeTab, setActiveTab] = useState('info');
  const [transactions, setTransactions] = useState<AmbassadorTransaction[]>([]);
  const [clients, setClients] = useState<ClientAmbassadorLink[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState<number>(0);
  const [adjustDescription, setAdjustDescription] = useState('');
  const [adjustLoading, setAdjustLoading] = useState(false);

  // Загрузка транзакций
  const loadTransactions = async () => {
    if (!ambassador) return;
    
    setTransactionsLoading(true);
    try {
      const response = await ambassadorsApi.getAmbassadorTransactions(ambassador.id, {
        limit: 10,  // ✅ Было per_page
        offset: 0,
      });
      setTransactions(response.data.data.transactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Загрузка привлеченных клиентов
  const loadClients = async () => {
    if (!ambassador) return;
    
    setClientsLoading(true);
    try {
      const response = await ambassadorsApi.getAmbassadorClients(ambassador.id, {
        limit: 10,  // ✅ Было per_page
        offset: 0,
      });
      setClients(response.data.data.clients);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  // Загрузка данных при смене вкладки
  useEffect(() => {
    if (visible && ambassador) {
      if (activeTab === 'transactions') {
        loadTransactions();
      } else if (activeTab === 'clients') {
        loadClients();
      }
    }
  }, [visible, ambassador, activeTab]);

  // Копирование промокода
  const handleCopyPromoCode = async () => {
    if (ambassador) {
      const success = await copyPromoCodeToClipboard(ambassador.promo_code);
      if (success) {
        message.success('Промокод скопирован');
      } else {
        message.error('Ошибка копирования');
      }
    }
  };

  // Копирование реферальной ссылки
  const handleCopyReferralLink = async () => {
    if (ambassador) {
      const link = getReferralLink(ambassador.promo_code);
      try {
        await navigator.clipboard.writeText(link);
        message.success('Ссылка скопирована');
      } catch (error) {
        message.error('Ошибка копирования');
      }
    }
  };

  // Обработка корректировки баланса
  const handleBalanceAdjust = async () => {
    if (!ambassador || !onBalanceAdjust) return;
    
    if (adjustAmount === 0) {
      message.warning('Введите сумму');
      return;
    }
    
    if (!adjustDescription.trim()) {
      message.warning('Введите описание');
      return;
    }
    
    setAdjustLoading(true);
    try {
      await onBalanceAdjust(ambassador.id, adjustAmount, adjustDescription);
      setAdjustModalVisible(false);
      setAdjustAmount(0);
      setAdjustDescription('');
      loadTransactions(); // Обновляем список транзакций
    } catch (error) {
      // Ошибка обрабатывается в хуке
    } finally {
      setAdjustLoading(false);
    }
  };

  if (!ambassador) {
    return null;
  }

  // Колонки для таблицы транзакций
  const transactionColumns = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('ru-RU'),
      width: 150,
    },
    {
      title: 'Тип',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (type: string) => (
        <Tag color={getTransactionTypeColor(type)}>
          {getTransactionTypeText(type)}
        </Tag>
      ),
      width: 120,
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: AmbassadorTransaction) =>
        formatTransactionAmount(amount, record.transaction_type),
      width: 120,
    },
    {
      title: 'Баланс после',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (balance: number) => formatBonusBalance(balance),
      width: 120,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Заказ',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (orderNumber: string | undefined) => orderNumber || '—',
      width: 120,
    },
  ];

  // Колонки для таблицы клиентов
  const clientColumns = [
    {
      title: 'Клиент',
      dataIndex: ['client', 'name'],
      key: 'client_name',
      render: (name: string, record: ClientAmbassadorLink) => (
        <div>
          <div>{record.client?.name || name}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {formatPhoneDisplay(record.client?.phone || '')}
          </div>
        </div>
      ),
    },
    {
      title: 'Дата привлечения',
      dataIndex: 'date_linked',
      key: 'date_linked',
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU'),
      width: 130,
    },
    {
      title: 'Заказов',
      dataIndex: 'total_orders',
      key: 'total_orders',
      width: 100,
    },
    {
      title: 'Потрачено',
      dataIndex: 'total_spent',
      key: 'total_spent',
      render: (amount: number) => formatBonusBalance(amount),
      width: 120,
    },
    {
      title: 'Последний заказ',
      dataIndex: 'last_order_date',
      key: 'last_order_date',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('ru-RU') : 'Нет заказов',
      width: 130,
    },
  ];

  return (
    <>
      <Modal
        title={`Амбассадор: ${ambassador.customer?.name || 'Загрузка...'}`}
        open={visible}
        onCancel={onClose}
        width={900}
        footer={[
          <Button key="close" onClick={onClose}>
            Закрыть
          </Button>,
        ]}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Вкладка: Основная информация */}
          <TabPane tab="Информация" key="info">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Статистика */}
              <Row gutter={16}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Баланс бонусов"
                      value={ambassador.ambassador_bonus_balance}
                      suffix="₸"
                      valueStyle={{ color: '#faad14' }}
                    />
                    {onBalanceAdjust && (
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setAdjustModalVisible(true)}
                        style={{ padding: 0, marginTop: 8 }}
                      >
                        Корректировать
                      </Button>
                    )}
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Привлечено клиентов"
                      value={ambassador.linked_clients_count || 0}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Продажи"
                      value={ambassador.total_lifetime_sales}
                      suffix="₸"
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Заработано"
                      value={ambassador.total_earned_bonuses || 0} 
                      suffix="₸"
                      valueStyle={{ color: '#13c2c2' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Информация о клиенте-амбассадоре */}
              <Descriptions title="Данные амбассадора" bordered size="small">
                <Descriptions.Item label="Имя" span={3}>
                  {ambassador.customer?.name}
                </Descriptions.Item>
                <Descriptions.Item label="Телефон" span={3}>
                  {formatPhoneDisplay(ambassador.customer?.phone || '')}
                </Descriptions.Item>
                <Descriptions.Item label="ID клиента" span={3}>
                  {ambassador.customer?.id}
                </Descriptions.Item>
              </Descriptions>

              {/* Информация об амбассадоре */}
              <Descriptions title="Данные амбассадора" bordered size="small">
                <Descriptions.Item label="Промокод" span={2}>
                  <Space>
                    <Tag color="blue" style={{ fontSize: 14 }}>
                      {ambassador.promo_code}
                    </Tag>
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={handleCopyPromoCode}
                    >
                      Копировать
                    </Button>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Статус" span={1}>
                  <Tag color={ambassador.is_active ? 'green' : 'red'}>
                    {ambassador.is_active ? 'Активен' : 'Неактивен'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Реферальная ссылка" span={3}>
                  <Space>
                    <Input
                      value={getReferralLink(ambassador.promo_code)}
                      readOnly
                      style={{ width: 400 }}
                    />
                    <Button
                      icon={<LinkOutlined />}
                      onClick={handleCopyReferralLink}
                    >
                      Копировать
                    </Button>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Дата активации" span={3}>
                  {formatActivationDate(ambassador.date_activated)}
                </Descriptions.Item>
              </Descriptions>

              {/* Текущая квалификация */}
              {ambassador.current_period_stats && (
                <Card title="Текущий период" size="small">
                  <Descriptions bordered size="small">
                    <Descriptions.Item label="Новых клиентов" span={1}>
                      {ambassador.current_period_stats.new_clients}
                    </Descriptions.Item>
                    <Descriptions.Item label="Заказов" span={1}>
                      {ambassador.current_period_stats.total_orders}
                    </Descriptions.Item>
                    <Descriptions.Item label="Продажи" span={1}>
                      {formatBonusBalance(ambassador.current_period_stats.total_sales)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Уровень" span={2}>
                      {ambassador.current_period_stats.qualified_level ? (
                        <Tag color={getQualificationLevelColor(ambassador.current_period_stats.qualified_level)}>
                          {getQualificationLevelText(ambassador.current_period_stats.qualified_level)}
                        </Tag>
                      ) : (
                        <Tag>Не квалифицирован</Tag>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ставка" span={1}>
                      {ambassador.current_period_stats.commission_rate
                        ? formatCommissionRate(ambassador.current_period_stats.commission_rate)
                        : '—'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}
            </Space>
          </TabPane>

          {/* Вкладка: Транзакции */}
          <TabPane tab="Транзакции" key="transactions">
            <Table
              dataSource={transactions}
              columns={transactionColumns}
              rowKey="id"
              loading={transactionsLoading}
              pagination={{ pageSize: 10 }}
              size="small"
              locale={{
                emptyText: <Empty description="Нет транзакций" />,
              }}
            />
          </TabPane>

          {/* Вкладка: Привлеченные клиенты */}
          <TabPane tab={`Клиенты (${ambassador.linked_clients_count || 0})`} key="clients">
            <Table
              dataSource={clients}
              columns={clientColumns}
              rowKey="id"
              loading={clientsLoading}
              pagination={{ pageSize: 10 }}
              size="small"
              locale={{
                emptyText: <Empty description="Еще нет привлеченных клиентов" />,
              }}
            />
          </TabPane>
        </Tabs>
      </Modal>

      {/* Модальное окно корректировки баланса */}
      <Modal
        title="Корректировка баланса"
        open={adjustModalVisible}
        onCancel={() => {
          setAdjustModalVisible(false);
          setAdjustAmount(0);
          setAdjustDescription('');
        }}
        onOk={handleBalanceAdjust}
        confirmLoading={adjustLoading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            Текущий баланс: <strong>{formatBonusBalance(ambassador.ambassador_bonus_balance)}</strong>
          </div>
          
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Сумма (положительная для начисления, отрицательная для списания)"
            value={adjustAmount}
            onChange={(val) => setAdjustAmount(val || 0)}
            prefix={<DollarOutlined />}
            addonAfter="₸"
          />
          
          <Input.TextArea
            placeholder="Описание операции"
            value={adjustDescription}
            onChange={(e) => setAdjustDescription(e.target.value)}
            rows={3}
          />
          
          {adjustAmount !== 0 && (
            <div>
              Новый баланс:{' '}
              <strong>
                {formatBonusBalance(ambassador.ambassador_bonus_balance + adjustAmount)}
              </strong>
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
};